import { useCallback, useMemo, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { generatePath, useParams } from 'react-router-dom';

import {
    Box,
    Button,
    Card,
    CardHeader,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    TextField,
    useTheme,
} from '@mui/material';
import { makeStyles } from 'makeStyles';

import { useDefaultHardwareBinding } from 'common/useDefaultHardwareBinding';

import { ActionButton } from 'commonComponents/ActionButton';
import { CommonFormComponents } from 'commonComponents/CommonFormComponents';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';
import { PageContainer } from 'commonComponents/PageContainer';

import { useUser } from 'api/authorization';
import { useCustomer } from 'api/customers';
import { useLicenseProductSerialNumbers } from 'api/licenses';
import { type ProductConfiguration, useProductConfigurations } from 'api/productConfigurations';
import { useCurrentProduct } from 'api/products';
import { ExpandLessIcon, ExpandMoreIcon } from 'icons';
import { CreateIcon } from 'icons/CreateIcon';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { useCurrentUserHasRole } from 'roles/helpers';
import { ROUTES } from 'routes/routes';

import {
    ConfigurationFormCardContent,
    LicenseCardActions,
    LicenseCardContent,
    useLicenseAndConfigurationForm,
} from './LicenseAndConfigurationForm/LicenseAndConfigurationForm';
import { isNoneHardwareBinding, licenseWithHWBindingExists } from './utils';

export const useStyles = makeStyles()(theme => ({
    textField: {
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(1),
        width: '100%',
    },
    cardHeaderContent: {
        width: '50%',
    },
    cardHeaderAction: {
        width: '50%',
        marginRight: 0,
    },
}));

export function getOwnerId({
    currentProduct,
    currentUser,
    customer,
    upgradeFrom,
    userIsLicenseManager,
}: {
    currentProduct: { is_application: boolean; adk_owner_id?: string };
    currentUser: { owner_id: string };
    customer?: { owner_id: string };
    upgradeFrom?: { ownerId: string };
    userIsLicenseManager: boolean;
}) {
    if (upgradeFrom?.ownerId) {
        return upgradeFrom.ownerId;
    }

    if (currentProduct?.is_application) {
        return currentProduct.adk_owner_id;
    }

    if (customer?.owner_id) {
        return customer.owner_id;
    }

    if (!userIsLicenseManager) {
        return currentUser?.owner_id || '';
    }
    return '';
}

function DialogConfirmSerialNumber({ confirmOpen, setConfirmOpen, pendingSubmit, trigger }) {
    const handleConfirm = () => {
        setConfirmOpen(false);
        if (pendingSubmit) {
            trigger(pendingSubmit);
        }
    };

    return (
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>A system with this serial number already exists</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    This license can only be activated using the System ID that is already linked to this serial number.
                </DialogContentText>
                <DialogContentText>Proceed?</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirmOpen(false)} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleConfirm} color="primary" variant="contained">
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function CreateLicense({ upgradeFrom = null }) {
    const { customerId } = useParams<{ customerId?: string }>();

    const { data: customer } = useCustomer(Number(customerId));

    const { classes } = useStyles();
    const { data: currentUser } = useUser();
    const currentUserHasRole = useCurrentUserHasRole();
    const userIsLicenseManager = currentUserHasRole(ROLE_DEFINITIONS.LicenseManager);
    const { data: currentProduct } = useCurrentProduct();
    const { data: productLicenses } = useLicenseProductSerialNumbers(currentProduct?.id);

    const disabledFields = useMemo(() => {
        const fields = new Set(upgradeFrom ? ['serial_number', 'user', 'ownerId'] : []);
        if (customerId) {
            fields.add('user');
        }
        if (customer?.owner_id) {
            fields.add('ownerId');
        }
        if (!userIsLicenseManager) {
            fields.add('ownerId');
        }
        return fields;
    }, [upgradeFrom, customerId, customer, userIsLicenseManager]);

    const defaultHardwareBinding = useDefaultHardwareBinding(currentProduct);

    const adHocProductConfiguration: Omit<ProductConfiguration, 'id'> = useMemo(
        () => ({
            additionalLicenseData: '',
            comment: '',
            features: currentProduct.applicationFeatures,
            hardwareBinding: defaultHardwareBinding,
            hasSerialNumber: true,
            inUse: false,
            internal: false,
            invalidated: false,
            isAdhocConfiguration: true,
            maxNumberOfConcurrentUsers: null,
            max_version: null,
            maximumActivations: 1,
            name: '',
            partNo: '',
            product: null,
            runtimeFeatures: [],
            supportValidity: 366,
            supportValidityUnit: 'd',
            validity: 366,
            validityUnit: 'd',
            can_sign_files: false,
        }),
        [currentProduct, defaultHardwareBinding]
    );

    const [open, setOpen] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(null);
    const theme = useTheme();

    const { data: productConfigurations, error } = useProductConfigurations(
        { product: currentProduct?.id, invalidated: false },
        Boolean(currentProduct)
    );

    const availableFeatures = currentProduct?.features?.filter(
        f => f.featureType === 'Feature' || f.featureType === 'ADK Feature'
    );

    const defaultProductConfiguration = useMemo(() => {
        return userIsLicenseManager
            ? (adHocProductConfiguration ?? null)
            : (productConfigurations?.find(e => e != null) ?? null);
    }, [userIsLicenseManager, adHocProductConfiguration, productConfigurations]);

    const [selectedProductConfigurationOverride, setSelectedProductConfigurationOverride] = useState(null);

    const selectedProductConfiguration = selectedProductConfigurationOverride ?? defaultProductConfiguration;

    const onSelectedProductConfigurationChanged = useCallback(
        event => setSelectedProductConfigurationOverride(event.target.value ?? null),
        []
    );

    function getProductName() {
        if (selectedProductConfiguration?.product?.name) {
            return selectedProductConfiguration.product.name;
        }
        if (selectedProductConfiguration?.isAdhocConfiguration && currentProduct) {
            return currentProduct.name;
        }
        return '';
    }

    const license = useMemo(
        () => ({
            customerId: customer?.id || '',
            user: upgradeFrom?.user ?? customer?.id ?? '',
            serial_number: upgradeFrom?.serial_number ?? '',
            sales_order_number: '',
            comment: '',
            ownerId: (() => getOwnerId({ currentProduct, customer, currentUser, upgradeFrom, userIsLicenseManager }))(),
        }),
        [currentProduct, currentUser, upgradeFrom, userIsLicenseManager, customer]
    );

    const isInternalCustomer = useMemo(() => customer?.is_internal ?? false, [customer]);

    const initialValues = useMemo(
        () => ({
            ...license,
            configuration: {
                ...selectedProductConfiguration,
                product: currentProduct,
                runtimeFeatures: selectedProductConfiguration?.runtimeFeatures ?? [],
            },
        }),
        [currentProduct, license, selectedProductConfiguration]
    );

    const cancelButtonRoute = useMemo(() => {
        if (upgradeFrom) {
            return generatePath(ROUTES.LICENSE_DETAIL, { id: upgradeFrom.id });
        }
        if (customerId) {
            return generatePath(ROUTES.CUSTOMER_DETAIL, { id: customerId });
        }
        return ROUTES.LICENSE_LIST;
    }, [upgradeFrom, customerId]);

    const submitButtonRoute = customerId
        ? _ => generatePath(ROUTES.CUSTOMER_DETAIL, { id: customerId })
        : (licenseId: number) => generatePath(ROUTES.LICENSE_DETAIL, { id: licenseId.toString() });

    const licenseAndConfigurationForm = useLicenseAndConfigurationForm({
        initialValues,
        submitTarget: submitButtonRoute,
    });
    const { formContext, trigger, isMutating } = licenseAndConfigurationForm;
    const { watch } = formContext;
    const internal = watch('internal');

    const SubmitComponent = useCallback(
        ({ inProgress }) => {
            const { formContext } = licenseAndConfigurationForm;
            return (
                <ActionButton
                    color="secondary"
                    data-testid="SubmitButton"
                    iconClass={CreateIcon}
                    inProgress={inProgress}
                    title="Create"
                    type="button"
                    variant="contained"
                    onClick={async () => {
                        const isValid = await formContext.trigger();
                        if (isValid) {
                            const values = formContext.getValues();
                            if (upgradeFrom) {
                                trigger(values);
                                return;
                            }

                            const newLicenseIsNoneHWBinding = isNoneHardwareBinding(
                                currentProduct,
                                values.configuration.hardwareBinding
                            );
                            if (newLicenseIsNoneHWBinding) {
                                trigger(values);
                                return;
                            }

                            const hasLicenseWithHWBinging = licenseWithHWBindingExists(
                                productLicenses || [],
                                values.serial_number
                            );
                            if (!hasLicenseWithHWBinging) {
                                trigger(values);
                                return;
                            }

                            setPendingSubmit(values);
                            setConfirmOpen(true);
                        }
                    }}
                />
            );
        },
        [currentProduct, licenseAndConfigurationForm, productLicenses, trigger, upgradeFrom]
    );

    if (error) {
        return null;
    }

    if (!productConfigurations || !licenseAndConfigurationForm || !currentProduct) {
        return <LoadingIndicator />;
    }

    if (!selectedProductConfiguration) {
        return <div />;
    }

    return (
        <PageContainer maxWidth="lg">
            <FormContainer formContext={formContext}>
                <CommonFormComponents />
                <DialogConfirmSerialNumber
                    confirmOpen={confirmOpen}
                    setConfirmOpen={setConfirmOpen}
                    pendingSubmit={pendingSubmit}
                    trigger={trigger}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(1),
                    }}
                >
                    <Card>
                        <CardHeader
                            classes={{ content: classes.cardHeaderContent, action: classes.cardHeaderAction }}
                            title={`Create new ${getProductName()} license`}
                            subheader={internal ? 'Only for internal use' : ''}
                            action={
                                productConfigurations.length > 0 && (
                                    <TextField
                                        className={classes.textField}
                                        data-testid="CreateLicense_ConfigurationSelect"
                                        label="Configuration"
                                        onChange={onSelectedProductConfigurationChanged}
                                        select
                                        value={selectedProductConfiguration}
                                        variant="outlined"
                                        title={`${selectedProductConfiguration.name}${
                                            selectedProductConfiguration.internal ? ' (Only for internal use)' : ''
                                        }`}
                                    >
                                        {userIsLicenseManager && (
                                            <MenuItem key="-1" value={adHocProductConfiguration as any}>
                                                -- None --
                                            </MenuItem>
                                        )}
                                        {productConfigurations.map(productConfiguration => {
                                            const label = `${productConfiguration.name}${
                                                productConfiguration.internal ? ' (Only for internal use)' : ''
                                            }`;
                                            return (
                                                <MenuItem
                                                    key={productConfiguration.id}
                                                    value={productConfiguration as any}
                                                    title={label}
                                                >
                                                    {label}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                )
                            }
                            slotProps={{
                                subheader: { variant: 'subtitle2', color: 'error' },
                            }}
                        />
                        <LicenseCardContent
                            license={license}
                            isNewLicense
                            disabledFields={Array.from(disabledFields)}
                            isInternalCustomer={isInternalCustomer}
                        />
                        <LicenseCardActions
                            cancelButtonRoute={cancelButtonRoute}
                            SubmitComponent={SubmitComponent}
                            isMutating={isMutating}
                        />
                    </Card>
                    <Card>
                        <CardHeader
                            onClick={() => setOpen(!open)}
                            subheader="Configuration Details"
                            action={
                                <IconButton size="large">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                            }
                        />
                        <Collapse in={open} timeout="auto">
                            <Divider />
                            <ConfigurationFormCardContent
                                availableFeatures={
                                    selectedProductConfiguration.isAdhocConfiguration
                                        ? availableFeatures
                                        : selectedProductConfiguration.features
                                }
                                selectedProductConfiguration={selectedProductConfiguration}
                            />
                        </Collapse>
                    </Card>
                </Box>
            </FormContainer>
        </PageContainer>
    );
}
